import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import { createGuilds, freeGuildTemplate, guildTemplateError, refusedPlan } from "./api";
import { DialogRow } from "./DialogRow";
import { sourceLink } from "./links";
// The guilds list's own vocabulary — «3 weeks ago», not «27 d ago». This
// dialog is drawing the age of a guild the operator will next meet on that
// list, and one guild with two ages is one guild the workflow cannot follow.
import { formatRelativeLong } from "../guilds/relativeTime";
import { formatCount, MAX_AGE_DAYS, plural } from "./roster";
import type { BotSource, CreateGuildsPlan, PlannedDraw } from "./types";
import { useDebounced } from "./useDebounced";
import styles from "./GuildTemplatesTab.module.css";

/**
 * «Create guilds» — the one button on this tab that does something
 * irreversible, and therefore the one dialog that shows its work first.
 *
 * The operator answers here the single question the table cannot: how OLD
 * should these guilds look. A guild created a second ago and already full of
 * members is the loudest «this is fake» tell there is, and waiting nine hours
 * for the join rate to fill it honestly makes a showcase useless on the day it
 * is needed. So the age is a control, its `created_at` is written that far
 * back, and the dialog says out loud that this is a fiction the operator chose.
 *
 * Three of its rules are worth reading before the code, because each is a
 * place where the obvious dialog would be wrong:
 *
 *   - NOTHING IS CREATED PART-WAY. Five guilds of six-to-twelve bots is up to
 *     sixty bots, and a `table` source may have thirty-four rows left. The
 *     plan is drawn against the source when the dialog opens AND checked again
 *     when the button is pressed — the population worker spends the same rows
 *     on its own five-minute schedule, so a 409 at that second is not a
 *     failure to explain away. The plan comes back with it, the red alert is
 *     redrawn from ITS numbers, and the operator lowers theirs.
 *   - VISIBILITY IS NOT A FIELD. It is `open`, always: a closed guild collects
 *     join requests and the leader who would answer them is a simulation that
 *     answers none. It is shown as a locked value rather than left out, because
 *     an operator who cannot see the setting goes looking for it.
 *   - THE PREVIEW IS DRAWN, NOT GUESSED. Each guild's age and starting size are
 *     rolled by the server and shown before anything is written.
 *
 * Mockup: t-103/bots-guild-templates.html §2.
 */

/** The dialog's own defaults, from the mockup: a guild between three days and
 *  a month old, six to twelve bots in it including the founder. */
const DEFAULT_AGE_MIN = "3";
const DEFAULT_AGE_MAX = "30";
const DEFAULT_MEMBERS_MIN = "6";
const DEFAULT_MEMBERS_MAX = "12";

/** The numbers as the boxes hold them: text, so an empty box is empty rather
 *  than a zero the plan would be drawn against. */
interface Draft {
  count: string;
  ageMin: string;
  ageMax: string;
  membersMin: string;
  membersMax: string;
}

interface Numbers {
  count: number;
  age_min_days: number;
  age_max_days: number;
  members_min: number;
  members_max: number;
}

interface Props {
  projectID: string;
  /** Set when the run was started from a row's menu: this template and no
   *  other. */
  templateIDs?: string[];
  initialCount: number;
  /** The named row's own name, for the case above. */
  onlyTemplate?: string;
  /**
   * «Create this guild again»: the one template of this run is SPENT, and the
   * row has to be freed before it can be spent a second time.
   *
   * That free happens inside this dialog's own run — after Create is pressed
   * and never before it. The row is the only record that this project stood
   * that guild up, so a Cancel here must leave it exactly as it was; freeing
   * on the way IN threw the history away before anybody had agreed to
   * anything. It is also why there is no plan to draw first: the server cannot
   * plan a run over a row that is still spent.
   */
  freeTemplateID?: string;
  /** The row was freed — the table behind this dialog is a state out of date
   *  whether or not the guild then went up. */
  onFreed?: () => void;
  /** A run left guilds standing that nobody agreed to. The dialog STAYS OPEN
   *  and names them; this only tells the table behind it to refetch. */
  onPartial?: () => void;
  /** Rows a guild name has taken — they are skipped by every run, and the
   *  helper says so rather than leaving «of N available» to be reverse
   *  engineered. */
  blocked: number;
  /** Those rows' names, as far as the table behind this dialog has loaded them.
   *  The helper names the first couple: a row an operator can go and rename is
   *  worth more than a count they have to go and resolve. */
  blockedNames: string[];
  /** `guild.max_members`, until the first plan answers with the server's own. */
  maxMembers: number;
  /** `bots.population.join_probability` and the target range — what happens to
   *  the guild after this dialog is done with it. */
  joinPercent: number;
  targetMembersMin: number;
  targetMembers: number;
  onClose: () => void;
  /** The run landed — the tab toasts it and refetches. */
  onDone: (plan: CreateGuildsPlan) => void;
}

export function CreateGuildsModal({
  projectID,
  templateIDs,
  initialCount,
  onlyTemplate,
  freeTemplateID,
  onFreed,
  onPartial,
  blocked,
  blockedNames,
  maxMembers,
  joinPercent,
  targetMembersMin,
  targetMembers,
  onClose,
  onDone,
}: Props) {
  const [draft, setDraft] = useState<Draft>({
    count: String(initialCount),
    ageMin: DEFAULT_AGE_MIN,
    ageMax: DEFAULT_AGE_MAX,
    membersMin: DEFAULT_MEMBERS_MIN,
    membersMax: DEFAULT_MEMBERS_MAX,
  });
  // The plan a refused run came back with. It replaces the preview until the
  // numbers move, which is what makes «the source has 34 rows» the source's
  // answer of a second ago rather than of when the dialog opened.
  const [refused, setRefused] = useState<CreateGuildsPlan | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // The free happened, and the create after it did not. The row is free NOW,
  // so the dialog stops telling the operator it is spent and starts behaving
  // like the ordinary one-template run it has become — plan included.
  const [freed, setFreed] = useState(false);
  const freedRef = useRef(false);
  const needsFree = !!freeTemplateID && !freed;
  /**
   * The run failed AND its rollback could not take everything back out, so
   * guilds nobody agreed to are standing.
   *
   * It keeps the dialog open, which is the whole point: this is the one outcome
   * the contract otherwise forbids, and «three of five are standing» is only
   * sayable if the three are NAMED. A toast that fades in five seconds is not
   * where an operator reads a list they now have to go and tidy up.
   */
  const [standing, setStanding] = useState<CreateGuildsPlan | null>(null);

  const settled = useDebounced(draft);
  // The run uses the SETTLED numbers, because those are the ones the plan on
  // screen was drawn for. While they disagree with the boxes, the button is
  // dead: typing «12» and pressing Create inside a quarter of a second would
  // otherwise create the five guilds of the plan nobody is looking at any more.
  const typing = settled !== draft;

  const numbers = numbersOf(settled);
  const fault = validate(numbers, maxMembers);

  const preview = useQuery({
    queryKey: [
      "project",
      projectID,
      "bots",
      "create-guilds",
      templateIDs ?? null,
      numbers,
    ],
    queryFn: () =>
      createGuilds(projectID, {
        ...numbers,
        template_ids: templateIDs,
        preview: true,
      }),
    // A spent row cannot be planned over — it is freed by the run itself, and
    // until then there is nothing for the server to draw.
    enabled: fault === null && !needsFree,
    retry: false,
    // NEVER from the cache. The key is the numbers, so nudging a count 5 → 4 →
    // 5 after a refusal — or closing and reopening the dialog — would serve the
    // plan drawn BEFORE the refusal, with `enough: true` and a live button,
    // seconds after the server said the table could not pay for it. The whole
    // reason this dialog re-asks is that the source moves under it: the worker
    // spends rows every five minutes, and the panel's 30 s freshness window is
    // exactly the interval in which the answer stops being true.
    staleTime: 0,
    gcTime: 0,
    // The previous plan stays on screen while the next one is drawn: a table
    // that vanished between two keystrokes would be a dialog blinking at the
    // operator rather than answering them.
    placeholderData: (prev) => prev,
  });

  const run = useMutation({
    mutationFn: async () => {
      // Both halves of «Create this guild again», in the order that makes
      // Cancel mean nothing happened.
      if (freeTemplateID && !freedRef.current) {
        await freeGuildTemplate(projectID, freeTemplateID);
        freedRef.current = true;
        onFreed?.();
      }
      // The echo, and the whole reason the button is honest: the ages and the
      // sizes in the table above were DRAWN, so a create call without them
      // would have the server draw a second set — different ages, different
      // sizes, a different bill in bots than the table the operator agreed to,
      // and a refusal on a total they never saw.
      //
      // It echoes the plan ON SCREEN, which is what was agreed to, and `count`
      // comes from the echo so the two cannot disagree. The server checks every
      // row against the ranges submitted beside it and refuses a plan it cannot
      // honour, rather than quietly drawing another one.
      const echo = echoOf(refused ?? preview.data ?? null);
      return createGuilds(projectID, {
        ...numbers,
        ...(echo ? { count: echo.length, plan: echo } : null),
        template_ids: templateIDs,
        preview: false,
      });
    },
    onSuccess: (result) => {
      if (result.partial) {
        setStanding(result);
        setProblem(null);
        onPartial?.();
        return;
      }
      onDone(result);
    },
    onError: (err) => {
      // Whatever went wrong, if the row was freed on the way in it is free now
      // — and a dialog that went on calling it spent would be describing the
      // table it just changed.
      if (freedRef.current) setFreed(true);
      // The worker spent the rows between this dialog opening and the button
      // being pressed. Nothing was created — the plan that came back with the
      // refusal says what the project can afford NOW, and the alert is redrawn
      // from it.
      const refusal = refusedPlan(err);
      if (refusal) {
        setRefused(refusal);
        setProblem(null);
        return;
      }
      setProblem(guildTemplateError(err));
    },
  });

  function edit(patch: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    setRefused(null);
    setProblem(null);
  }

  const plan = refused ?? preview.data ?? null;
  const previewError = preview.error ? guildTemplateError(preview.error) : null;
  const enough = plan?.enough ?? false;
  const ceiling = plan?.max_members ?? maxMembers;
  const guilds = plan?.guilds.length ?? 0;
  const shortOfBots = plan !== null && plan.rows_left < plan.total_bots;
  const shortOfTemplates = plan !== null && plan.available < numbers.count;
  // A run over a spent row has no plan to agree to: it is one guild from one
  // named template, and the numbers on screen are its whole description. What
  // it still cannot do is run against numbers that are not a plan, or numbers
  // the boxes have moved past.
  const canRun =
    standing === null &&
    (needsFree
      ? fault === null && !typing
      : !!plan && enough && fault === null && !typing);

  return (
    <Modal
      open
      width="lg"
      title="Create guilds from templates"
      onClose={onClose}
      closeDisabled={run.isPending}
      footer={
        <>
          <span className={styles.footNote}>
            Backdating <code>created_at</code> is a fiction the operator chooses
            — it is written to the guild row.
          </span>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={run.isPending}
          >
            {standing ? "Close" : "Cancel"}
          </Button>
          {/* The run is over and it went wrong; the only thing left to do here
              is read what is standing and close. */}
          {!standing && (
            <Button
              onClick={() => run.mutate()}
              disabled={!canRun}
              loading={run.isPending}
            >
              {needsFree
                ? "Free the row and create"
                : guilds > 0
                  ? `Create ${plural(guilds, "guild", "guilds")}`
                  : "Create guilds"}
            </Button>
          )}
        </>
      }
    >
      <DialogRow label="Templates">
        {onlyTemplate ? (
          <>
            <div className={styles.lockedValue}>
              «{onlyTemplate}» <span className={styles.lockNote}>— this row</span>
            </div>
            <div className={styles.helper}>
              Started from the row menu, so this run uses that template and no
              other.
            </div>
          </>
        ) : (
          <>
            <span className={styles.pair}>
              <span className={styles.countInput}>
                <Input
                  type="number"
                  min={1}
                  aria-label="Templates"
                  value={draft.count}
                  onChange={(e) => edit({ count: e.target.value })}
                />
              </span>
              <span className={styles.unit}>
                {plan ? `of ${formatCount(plan.available)} available` : "of …"}
              </span>
            </span>
            <div className={styles.helper}>
              Taken from the top of the table down.
              {blocked > 0 && ` ${excludedNote(blocked, blockedNames)}`}
            </div>
          </>
        )}
      </DialogRow>

      {/*
        The boxes OPEN at three days to a month, and the ceiling above them is
        ten years: a project standing up a showcase for a game that has been
        live for years has every right to a guild older than a month, so the
        mockup's «3 to 30» is a starting point and not a limit. What the limit
        closes is arithmetic — see MAX_AGE_DAYS, which the account age a bot is
        created with is held to as well.
      */}
      <DialogRow label="Guild age" hint="How long the guild will look like it has existed.">
        <span className={styles.pair}>
          <span className={styles.pairInput}>
            <Input
              type="number"
              min={0}
              max={MAX_AGE_DAYS}
              aria-label="Guild age, from"
              value={draft.ageMin}
              onChange={(e) => edit({ ageMin: e.target.value })}
            />
          </span>
          <span className={styles.dash}>–</span>
          <span className={styles.pairInput}>
            <Input
              type="number"
              min={0}
              max={MAX_AGE_DAYS}
              aria-label="Guild age, to"
              value={draft.ageMax}
              onChange={(e) => edit({ ageMax: e.target.value })}
            />
          </span>
          <span className={styles.unit}>days</span>
        </span>
        <div className={styles.helper}>
          Each guild draws its own age from this range, and its{" "}
          <code>created_at</code> is set that far back.
        </div>
      </DialogRow>

      <DialogRow
        label="Starting members"
        hint="Bots seated at creation, including the founder."
      >
        <span className={styles.pair}>
          <span className={styles.pairInput}>
            <Input
              type="number"
              min={1}
              aria-label="Starting members, from"
              value={draft.membersMin}
              onChange={(e) => edit({ membersMin: e.target.value })}
            />
          </span>
          <span className={styles.dash}>–</span>
          <span className={styles.pairInput}>
            <Input
              type="number"
              min={1}
              aria-label="Starting members, to"
              value={draft.membersMax}
              onChange={(e) => edit({ membersMax: e.target.value })}
            />
          </span>
          <span className={styles.unit}>bots</span>
        </span>
        <div className={styles.helper}>
          Their <code>joined_at</code> is spread across the guild&apos;s own age,
          oldest first — a guild that grew, not a guild that was poured. After
          that the ordinary <b>join rate</b> ({formatCount(joinPercent)}% per
          seat per sweep) takes it towards{" "}
          {targetMembersMin < targetMembers
            ? `a target of ${formatCount(targetMembersMin)} to ${formatCount(targetMembers)}`
            : `the target of ${formatCount(targetMembers)}`}
          , which each guild draws for itself.
        </div>
      </DialogRow>

      <DialogRow label="Visibility">
        {/* Not a field, and shown anyway: an operator who cannot see the
            setting goes looking for the screen that holds it. */}
        <div className={styles.lockedValue}>
          Open <span className={styles.lockNote}>— fixed</span>
        </div>
        <div className={styles.helper}>
          A closed or invite-only guild collects join requests, and a simulated
          leader answers none of them.
        </div>
      </DialogRow>

      <DialogRow label="Max members">
        <div className={styles.lockedValue}>
          {formatCount(ceiling)}{" "}
          <span className={styles.lockNote}>— from guild.max_members</span>
        </div>
      </DialogRow>

      {/*
        The one outcome that leaves the project changed in a way nobody agreed
        to, and the reason the contract forbids a silent partial: the guilds are
        NAMED, so «3 of 5» is something an operator can act on rather than
        something they have to go and reconstruct from the guilds list.
      */}
      {standing && (
        <Alert
          variant="danger"
          title={`${formatCount(standing.guilds.length)} of ${formatCount(
            standing.planned ?? standing.guilds.length,
          )} guilds are standing`}
        >
          The run stopped part-way and could not be undone, so these guilds
          exist:{" "}
          <b>{standing.guilds.map((g) => g.name).join(", ")}</b> —{" "}
          {plural(standing.total_bots, "bot", "bots")} seated between them. The
          rest was not created.
          {standing.reason && (
            <>
              {" "}
              <span className={styles.mono}>{standing.reason}</span>
            </>
          )}{" "}
          Their rows are spent; delete a guild here and «Create this guild
          again» frees its row.
        </Alert>
      )}
      {fault && !standing && (
        <Alert variant="danger" title="These numbers do not make a plan">
          {fault}
        </Alert>
      )}
      {previewError && !fault && (
        <Alert variant="danger" title="The plan could not be drawn">
          {previewError}
        </Alert>
      )}
      {problem && (
        <Alert variant="danger" title="Nothing was created">
          {problem}
        </Alert>
      )}

      <div className={styles.previewHead}>
        <span>What will be created</span>
        <span className={styles.mono}>
          {needsFree
            ? "the row is freed by this run, not by opening it"
            : "ages and sizes are drawn now, and shown before anything is written"}
        </span>
      </div>
      <div className={styles.card}>
        {needsFree ? (
          <p className={styles.emptyPreview}>
            «{onlyTemplate}» is <b>spent</b> — its guild is gone, and the row is
            still the record that this project stood it up. Pressing Create
            frees the row and founds the guild in one go, with the age and size
            chosen above; there is no plan to draw before that, because a spent
            row is not a row a run can be planned over. Cancel here and the row
            stays exactly as it is.
          </p>
        ) : !plan || plan.guilds.length === 0 ? (
          <p className={styles.emptyPreview}>
            {plan
              ? "No template is usable right now — every row is either spent or blocked by its name."
              : "Drawing the plan…"}
          </p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Guild</th>
                <th className={styles.num} scope="col">
                  Age
                </th>
                <th className={styles.num} scope="col">
                  Members
                </th>
                <th scope="col">Founder</th>
                <th scope="col">First join</th>
              </tr>
            </thead>
            <tbody>
              {plan.guilds.map((g) => (
                <tr key={g.template_id}>
                  <td className={styles.whoName}>{g.name}</td>
                  <td className={styles.num}>{formatCount(g.age_days)} d</td>
                  <td className={styles.num}>{formatCount(g.members)}</td>
                  {/* The founder's name is drawn when the guild is founded, not
                      when the plan is: showing one the run would then not use
                      is worse than showing none. */}
                  <td className={styles.mono}>{g.founder || "drawn on create"}</td>
                  <td className={styles.mono}>
                    {formatRelativeLong(g.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {plan && plan.guilds.length > 0 && (
          <div className={styles.cardFoot}>
            <span>
              {plural(plan.guilds.length, "guild", "guilds")} ·{" "}
              {plural(plan.total_bots, "bot", "bots")}
              {/* `plural`, not `formatCount`: this said «1 rows left after
                  this» for a year, one line below a neighbour that got it
                  right. */}
              {enough &&
                ` · ${leftOver(plan.source, plan.rows_left - plan.total_bots)} after this`}
            </span>
            <span className={styles.mono}>
              source: {SOURCE_WORD[plan.source]} ·{" "}
              {leftOver(plan.source, plan.rows_left)}
            </span>
          </div>
        )}
      </div>

      {shortOfBots && plan && (
        <Alert
          variant="danger"
          title={`The source has ${sourceUnits(plan.source, plan.rows_left)} and this needs ${plural(plan.total_bots, "bot", "bots")}`}
        >
          {formatCount(plan.total_bots - plan.rows_left)} of these seats cannot
          be filled. Nothing is created half-way: either lower the number of
          templates or the starting size, or{" "}
          {plan.source === "table"
            ? "import more rows into the source first."
            : "add more names to the pool first."}{" "}
          <Link to={sourceLink(projectID)}>Open Source ›</Link>
        </Alert>
      )}
      {shortOfTemplates && plan && (
        <Alert
          variant="danger"
          title={`${plural(plan.available, "template is", "templates are")} usable and this run asks for ${formatCount(numbers.count)}`}
        >
          Nothing is created half-way. Lower the number of templates, or add
          rows to the table — a row whose name a guild of this project already
          carries is not usable until it is renamed.
        </Alert>
      )}

      <div className={styles.alertStack}>
        <Alert
          variant="info"
          title="The founder is a bot, and it is the only place that is allowed"
        >
          A guild needs a leader, so the first bot seated founds it. Promoting a
          bot through the game API stays refused, and a bot is still never a
          successor. When living players arrive and the guild matures, the
          founder leaves like any other bot and the seat passes to the strongest
          living member — a guild with no living member left is dissolved,
          exactly as today.
        </Alert>
        <Alert
          variant="warn"
          title="The founder will not drift away on its own"
        >
          Ordinary churn takes bots out of guilds at random. The founder is
          exempt while the guild holds no living players — otherwise the first
          roll of the dice would dissolve a showcase guild created a minute
          earlier.
        </Alert>
      </div>
    </Modal>
  );
}

/**
 * Which rows this run will step over, NAMED.
 *
 * «1 row is skipped» is a number an operator has to go and look up; «"Vowel
 * Vikings" is excluded — its name is taken» is the row itself, and renaming it
 * is the whole of the fix. Two names and then a count, because a run over a
 * table with nine blocked rows should not print nine names into a helper line.
 *
 * The count is the TABLE's and the names are only of rows actually loaded, so a
 * filtered page falls back to the count rather than naming two out of a set it
 * cannot see all of.
 */
function excludedNote(blocked: number, names: string[]): string {
  if (names.length === 0) {
    return `${plural(blocked, "row is", "rows are")} skipped — the name is taken.`;
  }
  const shown = names.slice(0, 2).map((n) => `«${n}»`);
  const rest = blocked - shown.length;
  const subject =
    rest > 0
      ? `${shown.join(", ")} and ${formatCount(rest)} more are`
      : `${shown.join(" and ")} ${shown.length === 1 ? "is" : "are"}`;
  return `${subject} excluded — the name is taken.`;
}

/** What the source is called in the one place the dialog names it. */
const SOURCE_WORD: Record<BotSource, string> = {
  generated: "generated",
  table: "table",
};

/**
 * What the source is counted in.
 *
 * `rows_left` is a number in BOTH modes and means the same thing in both — how
 * many more bots the source could produce — but «34 rows» is wrong about a
 * generated project, which spends NAMES out of a pool and has no rows at all.
 * The mode picks the noun; the number is the server's either way.
 */
function sourceUnits(source: BotSource, n: number): string {
  return source === "table"
    ? plural(n, "row", "rows")
    : plural(n, "name", "names");
}

/** The same, as the remainder the dialog subtracts a run from. */
function leftOver(source: BotSource, n: number): string {
  return `${sourceUnits(source, n)} left`;
}

/**
 * The previewed plan as the create call carries it back, or null when there is
 * none to carry — «Create this guild again» has no plan to draw over a spent
 * row, and an empty table has nothing in it.
 *
 * Three fields and no more: a request has no business asserting a guild id, a
 * founder or a `created_at`. The first two do not exist yet, and the third is
 * derived from the age at the moment of writing, so a shape that carried them
 * would be a shape somebody could lie in.
 */
function echoOf(plan: CreateGuildsPlan | null): PlannedDraw[] | null {
  if (!plan || plan.guilds.length === 0) return null;
  return plan.guilds.map((g) => ({
    template_id: g.template_id,
    age_days: g.age_days,
    members: g.members,
  }));
}

function numbersOf(d: Draft): Numbers {
  return {
    count: toInt(d.count),
    age_min_days: toInt(d.ageMin),
    age_max_days: toInt(d.ageMax),
    members_min: toInt(d.membersMin),
    members_max: toInt(d.membersMax),
  };
}

/** An empty or unreadable box is NaN rather than zero, so `validate` names it
 *  instead of the server drawing a plan for a number nobody typed. */
function toInt(text: string): number {
  const trimmed = text.trim();
  if (trimmed === "") return NaN;
  const n = Number(trimmed);
  return Number.isInteger(n) ? n : NaN;
}

/**
 * Why these numbers cannot be a plan, or null. It is CreateGuildsInput.validate
 * in the browser, refusing the same five things in the same words — the point
 * being that a range which runs backwards is answered before the round trip,
 * not that the server has stopped checking.
 */
function validate(n: Numbers, maxMembers: number): string | null {
  if (Object.values(n).some((v) => Number.isNaN(v))) {
    return "Every box takes a whole number.";
  }
  if (n.count < 1) return "A run creates at least one guild.";
  if (n.age_min_days < 0 || n.age_max_days < n.age_min_days) {
    return "The age range runs backwards.";
  }
  if (n.age_max_days > MAX_AGE_DAYS) {
    // Bounding the top bounds the whole range: the check above has already
    // established that the bottom is not above the top. Same reasoning, same
    // order, as the Go switch this mirrors.
    return `${formatCount(n.age_max_days)} days is past the ${formatCount(MAX_AGE_DAYS)}-day limit.`;
  }
  if (n.members_min < 1 || n.members_max < n.members_min) {
    return "The size range runs backwards.";
  }
  if (n.members_max > maxMembers) {
    return `${formatCount(n.members_max)} members is more than guild.max_members (${formatCount(maxMembers)}).`;
  }
  return null;
}
